export const NCRM_VIEW_PREFERENCES_STORAGE_KEY = "ncrm-view-preferences";

const normalizeText = (value) => {
  const normalized = String(value ?? "").trim();
  return normalized || null;
};

const normalizeItems = (items = []) => {
  const seen = new Set();
  return (Array.isArray(items) ? items : []).reduce((accumulator, item) => {
    const value = normalizeText(item?.value);
    if (!value || seen.has(value)) return accumulator;
    seen.add(value);
    accumulator.push({
      ...item,
      value,
    });
    return accumulator;
  }, []);
};

const readStorageSnapshot = (storage) => {
  if (!storage || typeof storage.getItem !== "function") return {};
  try {
    const raw = storage.getItem(NCRM_VIEW_PREFERENCES_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch (_) {
    return {};
  }
};

const writeStorageSnapshot = (storage, snapshot) => {
  if (!storage || typeof storage.setItem !== "function") return;
  try {
    storage.setItem(NCRM_VIEW_PREFERENCES_STORAGE_KEY, JSON.stringify(snapshot));
  } catch (_) {
    /* ignore storage issues */
  }
};

export const resolveViewPreferencePageKey = (route, fallback = null) => {
  const normalizedPath =
    normalizeText(route?.path) ||
    normalizeText(route?.fullPath && String(route.fullPath).split("?")[0]) ||
    normalizeText(route);
  return normalizedPath || normalizeText(fallback);
};

export const reorderPreferredViewItems = (items = [], preferredValue = null) => {
  const normalizedItems = normalizeItems(items);
  const normalizedPreferredValue = normalizeText(preferredValue);
  if (!normalizedPreferredValue) return normalizedItems;
  const preferredItem = normalizedItems.find((item) => item.value === normalizedPreferredValue);
  if (!preferredItem) return normalizedItems;
  return [
    preferredItem,
    ...normalizedItems.filter((item) => item.value !== normalizedPreferredValue),
  ];
};

export const loadPreferredView = ({
  pageKey,
  items = [],
  fallback = null,
  storage = globalThis?.localStorage,
} = {}) => {
  const normalizedItems = normalizeItems(items);
  const normalizedPageKey = normalizeText(pageKey);
  const normalizedFallback = normalizeText(fallback) || normalizedItems[0]?.value || null;
  if (!normalizedPageKey) return normalizedFallback;
  const allowedValues = new Set(normalizedItems.map((item) => item.value));
  const snapshot = readStorageSnapshot(storage);
  const storedValue = normalizeText(snapshot?.[normalizedPageKey]);
  if (!storedValue || !allowedValues.has(storedValue)) return normalizedFallback;
  return storedValue;
};

export const savePreferredView = ({
  pageKey,
  value,
  items = [],
  storage = globalThis?.localStorage,
} = {}) => {
  const normalizedItems = normalizeItems(items);
  const normalizedPageKey = normalizeText(pageKey);
  const normalizedValue = normalizeText(value);
  if (!normalizedPageKey || !normalizedValue) return null;
  const allowedValues = new Set(normalizedItems.map((item) => item.value));
  if (!allowedValues.has(normalizedValue)) return null;
  const snapshot = readStorageSnapshot(storage);
  snapshot[normalizedPageKey] = normalizedValue;
  writeStorageSnapshot(storage, snapshot);
  return normalizedValue;
};
